import * as React from "react";
import { CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { useMemo, useState, useEffect } from "react";
import { format, parseISO, isValid, startOfDay } from "date-fns";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";

/* ---------------------------------- */
/* Helpers */
/* ---------------------------------- */

function asISO(d?: Date | null) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`; // ✅ DB format YYYY-MM-DD
}

function parseISODate(iso: string): Date | null {
  if (!iso) return null;
  const d = parseISO(iso);
  return isValid(d) ? d : null;
}

function nice(iso: string) {
  const d = parseISODate(iso);
  return d ? format(d, "yyyy-MM-dd") : "";
}

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

/* ---------------------------------- */
/* Component */
/* ---------------------------------- */

export function DatePickerField({
  value,
  onChange,
  placeholder = "Pick a date",
  min,
  max,
  disabled,
  error,

  // ✅ UX
  defaultMonth,
}: {
  value: string; // YYYY-MM-DD
  onChange: (iso: string) => void;
  placeholder?: string;
  min?: Date;
  max?: Date;
  disabled?: boolean;
  error?: string | null;

  // ✅ what month to open first (DOB: pass maxDob)
  defaultMonth?: Date;
}) {
  const selected = useMemo(() => parseISODate(value), [value]);

  const minDay = useMemo(() => startOfDay(min ?? new Date(1900, 0, 1)), [min]);
  const maxDay = useMemo(() => startOfDay(max ?? new Date()), [max]);

  const initialMonth = useMemo(() => {
    const base = selected ?? defaultMonth ?? maxDay;
    return new Date(base.getFullYear(), base.getMonth(), 1);
  }, [selected, defaultMonth, maxDay]);

  // ✅ We control the month shown in the calendar (for custom header)
  const [month, setMonth] = useState<Date>(initialMonth);

  // keep month in sync if value/defaultMonth changes (open correct year/month)
  useEffect(() => {
    setMonth(initialMonth);
  }, [initialMonth]);

  const fromYear = minDay.getFullYear();
  const toYear = maxDay.getFullYear();

  const monthIndex = month.getMonth();
  const year = month.getFullYear();

  function clampMonth(y: number, m: number) {
    // clamp year
    const yy = Math.min(toYear, Math.max(fromYear, y));
    // clamp month within range if at edges
    let mm = m;

    if (yy === fromYear) {
      const minM = minDay.getMonth();
      mm = Math.max(minM, mm);
    }
    if (yy === toYear) {
      const maxM = maxDay.getMonth();
      mm = Math.min(maxM, mm);
    }

    return new Date(yy, mm, 1);
  }

  const canPrev = useMemo(() => {
    const prev = new Date(year, monthIndex - 1, 1);
    return prev >= new Date(minDay.getFullYear(), minDay.getMonth(), 1);
  }, [year, monthIndex, minDay]);

  const canNext = useMemo(() => {
    const next = new Date(year, monthIndex + 1, 1);
    return next <= new Date(maxDay.getFullYear(), maxDay.getMonth(), 1);
  }, [year, monthIndex, maxDay]);

  return (
    <div className="space-y-1.5">
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className={cn(
              "w-full h-11 rounded-xl border border-border bg-background px-3",
              "flex items-center justify-between gap-2 text-left transition",
              "hover:bg-muted/30",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              disabled && "opacity-60 cursor-not-allowed",
              error && "border-destructive/60 focus-visible:ring-destructive/30"
            )}
          >
            <span className={cn("text-sm truncate", value ? "text-foreground" : "text-muted-foreground")}>
              {value ? nice(value) : placeholder}
            </span>

            <span className="h-8 w-8 rounded-lg border border-border bg-muted/20 grid place-items-center shrink-0">
              <CalendarIcon className="h-4 w-4 text-muted-foreground" />
            </span>
          </button>
        </PopoverTrigger>

        <PopoverContent className="w-auto p-3" align="start">
          {/* ✅ Custom header (like your screenshot) */}
          <div className="mb-2 flex items-center justify-between gap-2">
            <button
              type="button"
              disabled={!canPrev}
              onClick={() => canPrev && setMonth(clampMonth(year, monthIndex - 1))}
              className={cn(
                "h-9 w-9 rounded-lg border border-border bg-background grid place-items-center",
                "hover:bg-muted/40",
                !canPrev && "opacity-50 cursor-not-allowed"
              )}
              aria-label="Previous month"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            <div className="flex items-center gap-2">
              {/* Month dropdown */}
              <select
                value={monthIndex}
                onChange={(e) => setMonth(clampMonth(year, Number(e.target.value)))}
                className={cn(
                  "h-9 rounded-lg border border-border bg-background px-3 text-sm",
                  "focus:outline-none focus:ring-2 focus:ring-ring"
                )}
              >
                {MONTHS.map((m, idx) => {
                  // hide months outside min/max when at edge years
                  if (year === fromYear && idx < minDay.getMonth()) return null;
                  if (year === toYear && idx > maxDay.getMonth()) return null;
                  return (
                    <option key={m} value={idx}>
                      {m}
                    </option>
                  );
                })}
              </select>

              {/* Year number input (with steppers like screenshot) */}
            {/* Year with custom steppers (always visible) */}
<div className="flex items-center overflow-hidden rounded-lg border border-border bg-background">
  <input
    inputMode="numeric"
    value={String(year)}
    onChange={(e) => {
      // allow typing, keep numbers only
      const raw = e.target.value.replace(/[^\d]/g, "");
      const next = raw ? Number(raw) : year;
      setMonth(clampMonth(next, monthIndex));
    }}
    className={cn(
      "h-9 w-[72px] px-3 text-sm bg-transparent outline-none",
      "focus:ring-0"
    )}
    aria-label="Year"
  />

  <div className="flex flex-col border-l border-border">
    <button
      type="button"
      className={cn(
        "h-[18px] w-8 grid place-items-center hover:bg-muted/40",
        year >= toYear && "opacity-50 cursor-not-allowed"
      )}
      disabled={year >= toYear}
      onClick={() => setMonth(clampMonth(year + 1, monthIndex))}
      aria-label="Increase year"
      title="Increase year"
    >
      ▲
    </button>

    <button
      type="button"
      className={cn(
        "h-[18px] w-8 grid place-items-center border-t border-border hover:bg-muted/40",
        year <= fromYear && "opacity-50 cursor-not-allowed"
      )}
      disabled={year <= fromYear}
      onClick={() => setMonth(clampMonth(year - 1, monthIndex))}
      aria-label="Decrease year"
      title="Decrease year"
    >
      ▼
    </button>
  </div>
</div>
            </div>

            <button
              type="button"
              disabled={!canNext}
              onClick={() => canNext && setMonth(clampMonth(year, monthIndex + 1))}
              className={cn(
                "h-9 w-9 rounded-lg border border-border bg-background grid place-items-center",
                "hover:bg-muted/40",
                !canNext && "opacity-50 cursor-not-allowed"
              )}
              aria-label="Next month"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          {/* Calendar grid */}
          <Calendar
            mode="single"
            month={month}
            onMonthChange={(m) => setMonth(new Date(m.getFullYear(), m.getMonth(), 1))}
            selected={selected ?? undefined}
            onSelect={(d) => onChange(asISO(d ?? null))}
            disabled={(d) => {
              const day = startOfDay(d);
              return day < minDay || day > maxDay;
            }}
            modifiersClassNames={{
              today: "text-muted-foreground font-normal", // don’t “highlight today”
            }}
            // hide built-in caption/nav (we already built our own)
            classNames={{
              caption: "hidden",
              nav: "hidden",
            }}
            initialFocus
          />

          {error ? <p className="mt-2 text-xs text-destructive">{error}</p> : null}
        </PopoverContent>
      </Popover>

      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}